import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import LoadingScreen from "./LoadingScreen";
import toast from "react-hot-toast";
import { LOGO_SRC } from "../constants/branding";
import useThrottleCooldown from "../hooks/useThrottleCooldown";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:8002";

const Verify = () => {
  const { verifyEmail } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [registrationGateReady, setRegistrationGateReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [verificationState, setVerificationState] = useState("initial"); // initial, success, error, expired
  const [token, setToken] = useState("");
  const [error, setError] = useState("");
  const [hasAttemptedVerification, setHasAttemptedVerification] = useState(false);
  const cooldown = useThrottleCooldown();
  const email = localStorage.getItem("pendingVerificationEmail");
  // Set by Signup.jsx right after a successful registration so we can
  // render a more celebratory / clearer post-signup state. Cleared once
  // the user leaves the page.
  const [justRegistered, setJustRegistered] = useState(
    localStorage.getItem("justRegistered") === "1"
  );
  useEffect(() => {
    if (justRegistered) {
      localStorage.removeItem("justRegistered");
    }
  }, [justRegistered]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}/api/auth/registration-options/`
        );
        if (!res.ok) throw new Error("options");
        const data = await res.json();
        if (cancelled) return;
        if (data.skip_email_verification) {
          toast.success(
            "Email verification is not required. Sign in with your account."
          );
          navigate("/login", { replace: true });
          return;
        }
      } catch {
        // If API is unreachable, still allow the verify page
      }
      if (!cancelled) setRegistrationGateReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  useEffect(() => {
    if (!registrationGateReady || hasAttemptedVerification) return;

    const uidFromUrl = searchParams.get("uid");
    const tokenFromUrl = searchParams.get("token");
    if (uidFromUrl && tokenFromUrl) {
      setToken(tokenFromUrl);
      setHasAttemptedVerification(true);
      handleVerification(uidFromUrl, tokenFromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registrationGateReady, searchParams, hasAttemptedVerification]);

  const handleVerification = async (uid, verificationToken = token) => {
    if (!verificationToken || !uid) {
      setError("Invalid verification link");
      return;
    }

    if (isLoading) return;

    setIsLoading(true);
    setError("");
    setHasAttemptedVerification(true);

    try {
      const verifyResp = await verifyEmail(uid, verificationToken);
      setVerificationState("success");

      const verifiedEmail = localStorage.getItem("pendingVerificationEmail");
      const verifiedName =
        verifyResp?.user?.name ||
        verifyResp?.user?.email?.split("@")[0] ||
        verifiedEmail?.split("@")[0] ||
        "";
      localStorage.removeItem("pendingVerificationEmail");

      // Trigger the dashboard welcome toast on the user's first sign-in
      // after verification. Cleared by Dashboard on first paint.
      localStorage.setItem("welcomeNewUser", "1");
      localStorage.setItem("welcomeNewUserName", verifiedName);

      toast.success("Email verified successfully! Redirecting to login...", {
        id: "email-verified",
        duration: 2000,
      });

      setTimeout(() => {
        navigate("/login");
      }, 2000);
    } catch (err) {
      console.error("Verification error:", err);

      if (err.message.includes("expired")) {
        setVerificationState("expired");
        setError("Verification token has expired. Please request a new one.");
      } else if (err.message.includes("invalid")) {
        setVerificationState("error");
        setError("Invalid verification token. Please check and try again.");
      } else {
        setVerificationState("error");
        setError(err.message || "Verification failed. Please try again.");
      }
      toast.error(err.message || "Verification failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!email) {
      toast.error("No email address found. Please sign up again.");
      navigate("/signup");
      return;
    }

    setIsLoading(true);
    try {
      const authService = (await import("../services/authService")).default;
      await authService.resendVerificationEmail(email);
      toast.success("Verification email sent! Please check your inbox.");
      setError("");
    } catch (err) {
      console.error("Error resending verification email:", err);
      // 429 — throttled. Start cooldown so the button stays disabled.
      if (err.code === "throttled" || err.response?.status === 429) {
        const seconds = Math.max(1, Number(err.retryAfter) || 60);
        cooldown.start(seconds);
        const friendly = `Too many requests. Try again in ${
          seconds >= 60 ? `${Math.ceil(seconds / 60)} min` : `${seconds}s`
        }.`;
        toast.error(friendly, { duration: 6000 });
        setError(friendly);
      } else {
        toast.error(
          err.message || "Failed to resend verification email. Please try again."
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (!registrationGateReady || isLoading) {
    return (
      <LoadingScreen title="Verifying" subtitle="Checking your link…" />
    );
  }

  return (
    <div className="relative flex min-h-screen overflow-hidden bg-[#131313] text-white">
      <div
        className="pointer-events-none fixed inset-0 z-0"
        aria-hidden="true"
      >
        <div className="absolute inset-0 bg-[#131313]" />
        <div className="absolute -left-1/4 top-0 h-[min(70vh,520px)] w-[min(90vw,720px)] rounded-full bg-primary/[0.07] blur-[100px]" />
        <div className="absolute bottom-0 right-0 h-[min(50vh,400px)] w-[min(80vw,560px)] rounded-full bg-emerald-500/[0.05] blur-[90px]" />
      </div>

      <div className="relative z-10 flex w-full items-center justify-center px-4 py-12 sm:px-6">
        <motion.div
          className="w-full max-w-md"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: "easeOut" }}
        >
          <motion.div
            className="mb-8 flex justify-start"
            initial={{ opacity: 0, x: -12 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.08, duration: 0.45, ease: "easeOut" }}
          >
            <Link
              to="/"
              className="font-secondary inline-flex items-center rounded-sm px-2 py-2 text-[15px] text-white/65 transition-colors hover:bg-white/[0.06] hover:text-white"
            >
              <svg
                className="mr-2 h-5 w-5 shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
              Back to Home
            </Link>
          </motion.div>

          <motion.div
            className="rounded-sm border border-white/10 bg-landing-section p-8 sm:p-10"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.12, duration: 0.5, ease: "easeOut" }}
          >
            <div className="mb-8 text-center">
              <img
                src={LOGO_SRC}
                alt="DataNodes"
                width={127}
                height={41}
                className="mx-auto mb-6 h-10 w-auto sm:h-11"
              />
              <h2 className="font-title text-2xl font-bold tracking-tight text-white sm:text-[28px]">
                {verificationState === "success" ? (
                  "Email verified"
                ) : justRegistered ? (
                  <>
                    Account created &mdash;{" "}
                    <span className="text-primary">check your email</span>
                  </>
                ) : (
                  <>
                    Verify your{" "}
                    <span className="text-primary">email address</span>
                  </>
                )}
              </h2>
              <p className="font-secondary mt-3 text-[17px] leading-relaxed text-white/65">
                {email ? (
                  <>
                    We sent a verification link to{" "}
                    <span className="font-medium text-white/90">{email}</span>.
                    Click it to activate your account.
                  </>
                ) : (
                  "We sent you a verification link. Click it from your email to continue."
                )}
              </p>
            </div>

            {verificationState === "success" ? (
              <div className="space-y-6 text-center">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full border border-emerald-500/30 bg-emerald-500/10">
                  <svg
                    className="h-8 w-8 text-emerald-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <p className="font-secondary font-medium text-emerald-300/90">
                  Your email has been verified successfully.
                </p>
                <p className="font-secondary text-[15px] text-white/55">
                  Redirecting to sign in…
                </p>
              </div>
            ) : (
              <>
                {error && (
                  <div
                    className="font-secondary mb-6 rounded-sm border border-red-500/25 bg-red-500/10 px-4 py-3 text-sm text-red-300"
                    role="alert"
                  >
                    {error}
                  </div>
                )}

                {!searchParams.get("token") && (
                  <div className="space-y-5">
                    <div className="rounded-sm border border-white/10 bg-[#0c0c0c] px-4 py-5 text-center">
                      <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full border border-primary/25 bg-primary/10">
                        <svg
                          className="h-6 w-6 text-primary"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          aria-hidden
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 8l7.5 5.25a2 2 0 002.5 0L21 8m-18 8a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8z"
                          />
                        </svg>
                      </div>
                      <p className="font-secondary text-[15px] text-white/75">
                        Check your inbox for the verification email and click the
                        link to continue. The link expires in 24 hours.
                      </p>
                    </div>

                    <motion.button
                      type="button"
                      onClick={handleResendVerification}
                      disabled={cooldown.isCoolingDown}
                      className="font-secondary mt-2 flex w-full items-center justify-center rounded-sm border border-primary bg-primary px-6 py-4 text-[15px] font-bold leading-none text-[#0a0a0a] transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
                      whileHover={cooldown.isCoolingDown ? {} : { scale: 1.01 }}
                      whileTap={cooldown.isCoolingDown ? {} : { scale: 0.99 }}
                    >
                      {cooldown.isCoolingDown
                        ? cooldown.label("Try again in")
                        : "Resend verification email"}
                    </motion.button>
                  </div>
                )}

                {(verificationState === "error" ||
                  verificationState === "expired") && (
                  <div className="mt-8 text-center">
                    <p className="font-secondary text-[15px] text-white/65">
                      Link expired or didn&apos;t work?{" "}
                      <button
                        type="button"
                        onClick={handleResendVerification}
                        className="font-semibold text-primary transition-colors hover:text-primary-hover"
                      >
                        Resend verification email
                      </button>
                    </p>
                  </div>
                )}

                <div className="mt-6">
                  <Link
                    to="/login"
                    className="font-secondary flex w-full items-center justify-center rounded-sm border border-white/15 px-6 py-3 text-[15px] font-semibold text-white/80 transition-colors hover:bg-white/[0.06] hover:text-white"
                  >
                    Back to sign in
                  </Link>
                </div>

                <div className="mt-4 text-center">
                  <p className="font-secondary text-[14px] text-white/55">
                    Wrong email?{" "}
                    <Link
                      to="/signup"
                      className="font-semibold text-primary transition-colors hover:text-primary-hover"
                    >
                      Sign up again
                    </Link>
                  </p>
                </div>
              </>
            )}
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default Verify;
